import React, { useState } from 'react';

export default function HeaderWithControls({
  student,
  dateRange,
  attendanceRate = 0, // Keep the prop name for backward compatibility
  students = [],
  onStudentSelect,
  dateRangeOptions = [],
  selectedDateRange = 'last7Days',
  onDateRangeChange,
  customDateRange,
  onCustomDateChange
}) {
  // Ensure attendanceRate is always a valid format - handle both string fraction and number types
  const safeAttendanceScore = (() => {
    if (typeof attendanceRate === 'string' && attendanceRate.includes('/')) {
      return attendanceRate; // Already a fraction string
    }
    if (typeof attendanceRate === 'number' && !isNaN(attendanceRate)) {
      return `${attendanceRate}/100`; // Convert percentage to fraction format
    }
    if (typeof attendanceRate === 'string') {
      const parsed = parseFloat(attendanceRate);
      return !isNaN(parsed) ? `${parsed}/100` : '0/0';
    }
    return '0/0';
  })();
  
  // Helper function to calculate percentage for color coding
  const getPercentageFromFraction = (fraction) => {
    if (typeof fraction !== 'string' || !fraction.includes('/')) {
      return 0;
    }
    const [numerator, denominator] = fraction.split('/').map(parseFloat);
    if (isNaN(numerator) || isNaN(denominator) || denominator === 0) {
      return 0;
    }
    return (numerator / denominator) * 100;
  };

  const attendancePercentage = getPercentageFromFraction(safeAttendanceScore);
  
  const [isStudentDropdownOpen, setIsStudentDropdownOpen] = useState(false);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  
  // Local state for custom date inputs (don't trigger parent updates until Apply is clicked)
  const [localCustomDates, setLocalCustomDates] = useState({
    startDate: customDateRange?.startDate || '',
    endDate: customDateRange?.endDate || ''
  });

  // Filter students for search
  const filteredStudents = students.filter(student => {
    const fullName = `${student.first_name} ${student.last_name}`.toLowerCase();
    return fullName.includes(studentSearchTerm.toLowerCase());
  });

  // Handle student selection
  const handleStudentSelect = (selectedStudent) => {
    if (onStudentSelect) {
      onStudentSelect(selectedStudent);
    }
    setIsStudentDropdownOpen(false);
    setStudentSearchTerm('');
  };

  // Handle date range selection
  const handleDateRangeSelect = (range) => {
    if (range === 'custom') {
      // For custom range, don't close the dropdown and don't trigger change yet
      // Let user input dates first
      return;
    }
    
    if (onDateRangeChange) {
      onDateRangeChange(range);
    }
    setIsDateDropdownOpen(false);
  };

  const getRateColor = (rate) => {
    let numericRate = 0;
    if (typeof rate === 'number' && !isNaN(rate)) {
      numericRate = rate;
    } else if (typeof rate === 'string') {
      const parsed = parseFloat(rate);
      numericRate = !isNaN(parsed) ? parsed : 0;
    }
    
    if (numericRate >= 95) return 'text-green-600';
    if (numericRate >= 85) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (!student) {
    return (
      <div className="mb-6 bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-slate-400 to-slate-500 flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            
            <div className="relative">
              {/* Student Selection Dropdown */}
              <button
                onClick={() => setIsStudentDropdownOpen(!isStudentDropdownOpen)}
                className="text-left hover:bg-slate-50 px-3 py-2 rounded-md transition-colors border border-slate-300"
              >
                <div className="flex items-center">
                  <span className="text-slate-600 mr-2">Select a student</span>
                  <svg className={`w-4 h-4 text-slate-400 transition-transform ${isStudentDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              {/* Student Dropdown */}
              {isStudentDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsStudentDropdownOpen(false)}
                  />
                  <div className="absolute z-20 left-0 mt-1 bg-white shadow-lg border border-slate-200 rounded-lg max-h-60 overflow-hidden min-w-64">
                    {/* Search Input */}
                    <div className="p-2 border-b border-slate-200">
                      <input
                        type="text"
                        placeholder="Search students..."
                        value={studentSearchTerm}
                        onChange={(e) => setStudentSearchTerm(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        autoFocus
                      />
                    </div>
                    
                    {/* Student List */}
                    <div className="max-h-40 overflow-y-auto">
                      {filteredStudents.map((studentOption) => (
                        <button
                          key={studentOption.id}
                          onClick={() => handleStudentSelect(studentOption)}
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center space-x-3 text-slate-700"
                        >
                          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-medium text-white">
                              {studentOption.first_name?.charAt(0)}{studentOption.last_name?.charAt(0)}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {studentOption.first_name} {studentOption.last_name}
                            </p>
                            {studentOption.school && (
                              <p className="text-xs text-slate-500 truncate">
                                {typeof studentOption.school === 'object' ? studentOption.school.name : studentOption.school}
                              </p>
                            )}
                          </div>
                        </button>
                      ))}
                      {filteredStudents.length === 0 && (
                        <div className="px-3 py-2 text-sm text-slate-500 text-center">
                          {students.length === 0 ? 'No students available' : 'No students found'}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 bg-white rounded-lg shadow-sm border border-slate-200 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
            <span className="text-lg font-medium text-white">
              {student.first_name?.charAt(0)}{student.last_name?.charAt(0)}
            </span>
          </div>
          
          <div className="relative">
            {/* Clickable Student Name with Dropdown */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsStudentDropdownOpen(!isStudentDropdownOpen)}
                className="text-left hover:bg-slate-50 px-2 py-1 rounded-md transition-colors"
              >
                <h2 className="text-xl font-semibold text-slate-800 flex items-center">
                  {student.first_name} {student.last_name}
                  <svg className={`w-4 h-4 ml-2 text-slate-400 transition-transform ${isStudentDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </h2>
              </button>
            </div>

            {/* Clickable Date Range with Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsDateDropdownOpen(!isDateDropdownOpen)}
                className="text-slate-600 hover:bg-slate-50 px-2 py-1 rounded-md transition-colors flex items-center"
              >
                {dateRange?.startDate && dateRange?.endDate ? 
                  `${dateRange.startDate} to ${dateRange.endDate}` : 
                  'Select date range'
                }
                <svg className={`w-3 h-3 ml-2 text-slate-400 transition-transform ${isDateDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Date Range Dropdown */}
              {isDateDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsDateDropdownOpen(false)}
                  />
                  <div className="absolute z-20 left-0 mt-1 bg-white shadow-lg border border-slate-200 rounded-lg min-w-48">
                    {dateRangeOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleDateRangeSelect(option.value)}
                        className={`block w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${
                          selectedDateRange === option.value ? 'bg-blue-50 text-blue-600' : 'text-slate-700'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                    
                    {/* Custom Date Range Inputs - Always visible */}
                    <div className="border-t border-slate-200 p-3">
                      <div className="space-y-2">
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Start Date</label>
                          <input
                            type="date"
                            value={localCustomDates.startDate}
                            onChange={(e) => setLocalCustomDates(prev => ({ ...prev, startDate: e.target.value }))}
                            className="w-full px-2 py-1 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">End Date</label>
                          <input
                            type="date"
                            value={localCustomDates.endDate}
                            onChange={(e) => setLocalCustomDates(prev => ({ ...prev, endDate: e.target.value }))}
                            className="w-full px-2 py-1 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        
                        {/* Date Range Info */}
                        <div className="text-xs text-slate-500 mt-2">
                          Maximum range: 180 days
                        </div>
                        
                        {/* Apply Custom Range Button */}
                        <button
                          onClick={() => {
                            if (localCustomDates.startDate && localCustomDates.endDate) {
                              // Validate date range is not more than 180 days
                              const startDate = new Date(localCustomDates.startDate);
                              const endDate = new Date(localCustomDates.endDate);
                              const daysDifference = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
                              
                              if (daysDifference > 180) {
                                alert('Custom date range cannot exceed 180 days. Please select a shorter range.');
                                return;
                              }
                              
                              if (daysDifference < 0) {
                                alert('Start date must be before end date.');
                                return;
                              }
                              
                              // Update the parent component with the local dates
                              if (onCustomDateChange) {
                                onCustomDateChange('startDate', localCustomDates.startDate);
                                onCustomDateChange('endDate', localCustomDates.endDate);
                              }
                              if (onDateRangeChange) {
                                onDateRangeChange('custom');
                              }
                              setIsDateDropdownOpen(false);
                            }
                          }}
                          disabled={!localCustomDates.startDate || !localCustomDates.endDate}
                          className={`w-full mt-2 px-3 py-2 text-sm font-medium rounded transition-colors ${
                            localCustomDates.startDate && localCustomDates.endDate
                              ? 'bg-blue-600 text-white hover:bg-blue-700'
                              : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                          }`}
                        >
                          Apply Custom Range
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Student Dropdown */}
            {isStudentDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setIsStudentDropdownOpen(false)}
                />
                <div className="absolute z-20 left-0 mt-1 bg-white shadow-lg border border-slate-200 rounded-lg max-h-60 overflow-hidden min-w-64">
                  {/* Search Input */}
                  <div className="p-2 border-b border-slate-200">
                    <input
                      type="text"
                      placeholder="Search students..."
                      value={studentSearchTerm}
                      onChange={(e) => setStudentSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      autoFocus
                    />
                  </div>
                  
                  {/* Student List */}
                  <div className="max-h-40 overflow-y-auto">
                    {filteredStudents.map((studentOption) => (
                      <button
                        key={studentOption.id}
                        onClick={() => handleStudentSelect(studentOption)}
                        className={`w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center space-x-3 ${
                          student.id === studentOption.id ? 'bg-blue-50 text-blue-600' : 'text-slate-700'
                        }`}
                      >
                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-medium text-white">
                            {studentOption.first_name?.charAt(0)}{studentOption.last_name?.charAt(0)}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {studentOption.first_name} {studentOption.last_name}
                          </p>
                          {studentOption.school && (
                            <p className="text-xs text-slate-500 truncate">
                              {typeof studentOption.school === 'object' ? studentOption.school.name : studentOption.school}
                            </p>
                          )}
                        </div>
                      </button>
                    ))}
                    {filteredStudents.length === 0 && (
                      <div className="px-3 py-2 text-sm text-slate-500 text-center">
                        No students found
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        
        {/* Attendance Score Display */}
        <div className="text-right">
          <div className="text-2xl font-bold">
            <span className={getRateColor(attendancePercentage)}>
              {safeAttendanceScore}
            </span>
          </div>
          <div className="text-sm text-slate-500">Attendance Score</div>
        </div>
      </div>
    </div>
  );
}
